import { useContext, useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "./Navbar.styles.scss";
import LoginModal from "../common/LoginModal/LoginModal.component";
import { FirebaseAuthContext } from "../../auth/FirebaseAuth";
import {
  getLoggedInUserInfo,
  getUserDisplayName,
  isUserAuthenticated,
} from "../../utils/authUtil";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Tab, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { setThemeMode } from "../../store/theme/themeSlice";
import TimelineIcon from "@mui/icons-material/Timeline";
import CallIcon from "@mui/icons-material/Call";
import StoreIcon from "@mui/icons-material/Store";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import UserProfileMenu from "../UserProfileMenu/UserProfileMenu.component";
import Logo from "../Logo/Logo.component";
import MenuIcon from "@mui/icons-material/Menu";

import UserProfileMobileDrawer from "../UserProfileMobileDrawer/UserProfileMobileDrawer.component";
import AppMobileDrawer from "../AppMobileDrawer/AppMobileDrawer.component";
import { userDisplayName } from "../../utils/user";
import { userInfoSelector } from "../../store/user/user.selector";
import useTranslation from '../../translations/use-translation';


const avatarMobileSx = { marginLeft: "auto" };

const Navbar = () => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(isUserAuthenticated());
  const storage =localStorage.getItem('userTheme') || 'light';
  const [mode, setMode] = useState(storage);
  const loggedInUserDetails = useSelector(userInfoSelector)
  const [toggleAppMobileDrawer, setToggleAppMobileDrawer] = useState(false);
  const [toggleUserProfileMobileDrawer, setToggleUserProfileMobileDrawer] = useState(false);
  const dispatch = useDispatch();
  const [userInfo, setUserInfo] = useState(getLoggedInUserInfo());
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(getUserDisplayName());
  const [open, setOpen] = useState(false);
  const { logOutUser } = useContext(FirebaseAuthContext);
  const currentActiveTab = useLocation();
  const profileImageLetters = userDisplayName(userInfo?.displayName) || userInfo?.email?.[0]?.toUpperCase() || 'U'
  const theme = useTheme();
  const isMatch = useMediaQuery(theme.breakpoints.down("md"));
  const translate = useTranslation();

  const secretNavigationItems = isUserLoggedIn
    ? [
        {
          name: translate('APPLICATIONS'),
          icon: <TimelineIcon />,
          to: "/track-applications",
        },
      ]
    : [];
  const navigationItems = [
    { name: translate('SERVICES'), icon: <StoreIcon />, to: "/services" },
    ...secretNavigationItems,
    { name: translate('ABOUT_US'), icon: <GroupsIcon />, to: "/about-us" },
    { name: translate('CONTACT_US'), icon: <CallIcon />, to: "/contact-us" },
  ];

  const openLoginModal = () => {
    setOpen(!open);
  };
  const signOut = () => {
    logOutUser()
      .then(() => {
        localStorage.clear();
        setIsUserLoggedIn(false);
        setUserInfo(null);
        setToggleUserProfileMobileDrawer(false);
        setToggleAppMobileDrawer(false);
        navigate("/");
      })
      .catch((error) => {
        console.log(error);
      });
  };


  useEffect(() => {
    dispatch(setThemeMode(mode));
  }, [mode, dispatch]);
  useEffect(() => {
    setUserInfo(loggedInUserDetails?.loggedInUserInfo ?? getLoggedInUserInfo());
    setDisplayName(loggedInUserDetails?.userDisplayName ?? getUserDisplayName());
    setIsUserLoggedIn(isUserAuthenticated()); // Update login status
  }, [loggedInUserDetails]); // Update user info and display name when loggedInUserDetails changes

  return (
    <>
      <AppBar
        position="sticky"
        className="navbar-appbar"
      >
        <Toolbar>
          {isMatch ? (
            <>
              {!toggleAppMobileDrawer ? (
                <MenuIcon
                  className="navbar-icon"
                  onClick={() => {
                    setToggleAppMobileDrawer(!toggleAppMobileDrawer);
                    setToggleUserProfileMobileDrawer(false);
                  }}
                />
              ) : (
                <CloseIcon
                  className="navbar-icon"
                  onClick={() => {
                    setToggleAppMobileDrawer(!toggleAppMobileDrawer);
                    setToggleUserProfileMobileDrawer(false);
                  }}
                />
              )}
              <p className="navbar-brand">
                {translate('ONLINE_APPLICATIONS')}
              </p>

              {!isUserLoggedIn ? (
                <button
                  className="navbar-login-button"
                  onClick={openLoginModal}
                  aria-label="Login to your account"
                >
                  <span className="navbar-login-text">{translate('LOGIN')}</span>
                  <ChevronRightIcon className="navbar-login-icon" />
                </button>
              ) : (
                <>
                  {!toggleUserProfileMobileDrawer ? (
                    <Avatar
                      alt="user"
                      sx={avatarMobileSx}
                      onClick={() => {
                        setToggleAppMobileDrawer(false);
                        setToggleUserProfileMobileDrawer(!toggleUserProfileMobileDrawer);
                      }}
                      className="avatar-bg"
                    >
                      {profileImageLetters}
                    </Avatar>
                  ) : (
                    <CloseIcon
                      className="navbar-icon avatar-close-icon"
                      onClick={() => {
                        setToggleAppMobileDrawer(false);
                        setToggleUserProfileMobileDrawer(!toggleUserProfileMobileDrawer);
                      }}
                    />
                  )}
                  <UserProfileMobileDrawer
                    userInfo={userInfo}
                    signOut={signOut}
                    setToggleUserProfileMobileDrawer={setToggleUserProfileMobileDrawer}
                    toggleUserProfileMobileDrawer={toggleUserProfileMobileDrawer}
                  />
                </>
              )}

              <AppMobileDrawer
                toggleAppMobileDrawer={toggleAppMobileDrawer}
                setToggleAppMobileDrawer={setToggleAppMobileDrawer}
                openLogModal={openLoginModal}
                navigationItems={navigationItems}
                mode={mode}
                setMode={setMode}
                isUserLoggedIn={isUserLoggedIn}
              />
            </>
          ) : (
            <>
              <Logo />
              <nav className="navbar-tabs" role="navigation" aria-label="Main navigation">
                {navigationItems.map((navItem, index) => {
                  return (
                    <Tab
                      key={index}
                      label={navItem.name}
                      component={Link}
                      to={navItem.to}
                    />
                  );
                })}
              </nav>
              {isUserLoggedIn ? (
                <>
                  <UserProfileMenu
                    displayName={displayName}
                    userInfo={userInfo}
                    signOut={signOut}
                  />
                </>
              ) : (
                  <button
                    className="navbar-login-button navbar-login-button--desktop"
                    onClick={openLoginModal}
                    aria-label="Login to your account"
                  >
                    <span className="navbar-login-text">{translate('LOGIN')}</span>
                    <ChevronRightIcon className="navbar-login-icon" />
                  </button>
              )}

            </>
          )}
        </Toolbar>
      </AppBar>
      <LoginModal
        openLoginModal={open}
        setOpenLoginModal={setOpen}
      />
    </>
  );
};
export default Navbar;
